import React, { useState, useEffect } from 'react';
import SidebarLayout from "components/Layout/Layout";
import { API_URL } from "env";
import { useNavigate } from 'react-router-dom';
import { PencilSimpleIcon, TrashSimpleIcon } from '@phosphor-icons/react';
import { Table, Input, Button, Modal } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';

interface PointItem {
    id: number;
    code: string;
    cooldown_sec: number;
    expires_in_days: number;
    max_times_per_user: number;
    points_formatted: string;
}

const ListPoint: React.FC = () => {
    const [data, setData] = useState<PointItem[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [searchText, setSearchText] = useState('');

    const fetchData = async (page = 1, limit = 10) => {
        try {
            setLoading(true);
            let url = `${API_URL}api/v1/be/point_rules?page=${page}&limit=${limit}`;
            if (searchText.trim()) url += `&title=${encodeURIComponent(searchText)}`;
            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
            });
            if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
            const json = await response.json();
            setData(json.data.items || []);
            setCurrentPage(json.data.current_page || 1);
            setTotalItems(json.data.total || 0);
        } catch (err) {
            console.error("Lỗi khi gọi API:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(currentPage); }, [currentPage]);

    useEffect(() => {
        setCurrentPage(1);
        fetchData(1);
    }, [searchText]);

    const handleDelete = async (id: number) => {
        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xóa mục này không?",
            okText: "Xóa",
            cancelText: "Hủy",
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    setLoading(true);
                    const response = await fetch(`${API_URL}api/v1/be/point_rules/${id}`, {
                        method: "DELETE",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${localStorage.getItem("token")}`,
                        },
                    });
                    if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
                    const json = await response.json();
                    if (json.status) {
                        Modal.success({ title: "Thành công", content: "Xóa thành công!" });
                        await fetchData(currentPage);
                    } else {
                        Modal.error({ title: "Thất bại", content: "Xóa thất bại: " + (json.message || "Unknown error") });
                    }
                } catch (error: unknown) {
                    Modal.error({ title: "Lỗi", content: "Lỗi khi xóa: " + (error instanceof Error ? error.message : String(error)) });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const columns: ColumnsType<PointItem> = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: 'Code', dataIndex: 'code' },
        { title: 'Cooldown (sec)', dataIndex: 'cooldown_sec', width: 140 },
        { title: 'Expires (days)', dataIndex: 'expires_in_days', width: 140 },
        { title: 'Max Per User', dataIndex: 'max_times_per_user', width: 130 },
        { title: 'Points', dataIndex: 'points_formatted', width: 100 },
        {
            title: 'Action',
            key: 'actions',
            width: 100,
            render: (_, item) => (
                <div className="flex gap-2">
                    <button
                        onClick={() => navigate(`/add-point/edit/${item.id}`)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900 transition-colors cursor-pointer"
                    >
                        <PencilSimpleIcon size={16} />
                    </button>
                    <button
                        onClick={() => handleDelete(item.id)}
                        className="p-2 rounded bg-gray-100 dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900 transition-colors cursor-pointer text-red-500"
                    >
                        <TrashSimpleIcon size={16} />
                    </button>
                </div>
            ),
        },
    ];

    const handleTableChange = (pag: TablePaginationConfig) => {
        setCurrentPage(pag.current ?? 1);
    };

    return (
        <SidebarLayout
            title={<h4 className="text-2xl font-bold m-0! py-4">Points</h4>}
        >
            <div className="space-y-4">
                <div className="flex items-center gap-3">
                    <Input.Search
                        placeholder="Search..."
                        value={searchText}
                        onChange={e => setSearchText(e.target.value)}
                        allowClear
                        className="w-64"
                    />
                    <Button
                        type="primary"
                        className="ml-auto"
                        onClick={() => navigate('/add-point')}
                    >
                        + Add
                    </Button>
                </div>

                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    loading={loading}
                    pagination={{
                        current: currentPage,
                        pageSize: 10,
                        total: totalItems,
                        showSizeChanger: false,
                        showTotal: total => `Total ${total} items`,
                    }}
                    onChange={handleTableChange}
                />
            </div>
        </SidebarLayout>
    );
};

export default ListPoint;
